import type { CSSProperties } from "react";
import type { WeatherKind } from "@/greenpak/weather-conditions";

/**
 * Weather icons, drawn as SVG so they look the same on every device.
 *
 * These replace emoji, which turned into boxed hex codes ("01F 324") on
 * browsers with no font for them. Strokes use currentColor, so an icon takes
 * the colour of the text beside it in either theme; sun and lightning use
 * --wx-sun (set in globals.css) for a touch of warmth.
 *
 * Shapes adapted from Lucide (ISC licence), plus a sun-with-small-cloud for
 * "mostly clear", which Lucide does not have.
 *
 * Two outputs from one set of shapes: <WeatherIcon> for React, and
 * weatherIconSvg() for the Leaflet pin markers, which are built as HTML strings.
 */

type Shape = { d: string; sun?: boolean } | { circle: readonly [number, number, number]; sun?: boolean };

const CLOUD = "M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z";
const CLOUD_OPEN = "M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242";

const SHAPES: Record<Exclude<WeatherKind, "unknown">, readonly Shape[]> = {
  clear: [
    { circle: [12, 12, 4], sun: true },
    {
      d: "M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41",
      sun: true,
    },
  ],
  "mostly-clear": [
    { circle: [10, 10, 3.5], sun: true },
    { d: "M10 2.5V4M2.5 10H4M4.7 4.7l1.06 1.06M15.3 4.7l-1.06 1.06M4.7 15.3l1.06-1.06", sun: true },
    { d: "M13.5 21h5.75a2.75 2.75 0 0 0 .2-5.49 3.75 3.75 0 0 0-7.15 1.12A2.2 2.2 0 0 0 13.5 21Z" },
  ],
  "partly-cloudy": [
    { d: "M12 2v2M4.93 4.93l1.41 1.41M20 12h2M19.07 4.93l-1.41 1.41", sun: true },
    { d: "M15.947 12.65a4 4 0 0 0-5.925-4.128", sun: true },
    { d: "M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z" },
  ],
  overcast: [{ d: CLOUD }],
  fog: [{ d: CLOUD_OPEN }, { d: "M16 17H7M17 21H9" }],
  drizzle: [{ d: CLOUD_OPEN }, { d: "M8 19v1M8 14v1M16 19v1M16 14v1M12 21v1M12 16v1" }],
  rain: [{ d: CLOUD_OPEN }, { d: "M16 14v6M8 14v6M12 16v6" }],
  snow: [{ d: CLOUD_OPEN }, { d: "M8 15h.01M8 19h.01M12 17h.01M12 21h.01M16 15h.01M16 19h.01" }],
  storm: [{ d: "M6 16.326A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 .5 8.973" }, { d: "m13 12-3 5h4l-3 5", sun: true }],
};

const SUN_STYLE: CSSProperties = { stroke: "var(--wx-sun, currentColor)" };

function shapesFor(kind: WeatherKind): readonly Shape[] | null {
  return kind === "unknown" ? null : (SHAPES[kind] ?? null);
}

/** Renders nothing for an unknown condition — the label beside it still says "—". */
export function WeatherIcon({
  kind,
  size = 14,
  className,
}: {
  kind: WeatherKind;
  size?: number;
  className?: string;
}) {
  const shapes = shapesFor(kind);
  if (!shapes) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className ? `pbd-wx-icon ${className}` : "pbd-wx-icon"}
    >
      {shapes.map((shape, i) =>
        "circle" in shape ? (
          <circle
            key={i}
            cx={shape.circle[0]}
            cy={shape.circle[1]}
            r={shape.circle[2]}
            style={shape.sun ? SUN_STYLE : undefined}
          />
        ) : (
          <path key={i} d={shape.d} style={shape.sun ? SUN_STYLE : undefined} />
        ),
      )}
    </svg>
  );
}

/**
 * The same icon as an SVG string, for markup built outside React. Every value
 * in it comes from the constants above, so nothing needs escaping.
 */
export function weatherIconSvg(kind: WeatherKind, size = 12): string {
  const shapes = shapesFor(kind);
  if (!shapes) return "";
  const sun = ' style="stroke:var(--wx-sun, currentColor)"';
  const body = shapes
    .map((shape) =>
      "circle" in shape
        ? `<circle cx="${shape.circle[0]}" cy="${shape.circle[1]}" r="${shape.circle[2]}"${shape.sun ? sun : ""}/>`
        : `<path d="${shape.d}"${shape.sun ? sun : ""}/>`,
    )
    .join("");
  return `<svg class="pbd-wx-icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${body}</svg>`;
}
